import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Home, ShoppingBag, Megaphone, ShieldAlert, LogOut, Menu, X, User, History, LifeBuoy, BookOpen, Radio } from "lucide-react";
import { PLATFORM_META, PLATFORM_ORDER } from "@/lib/platforms";

// Section label with reserved height so items stay vertically aligned
// whether the sidebar is expanded or collapsed.
function SectionLabel({ expanded, children }) {
  return (
    <div className="h-7 flex items-end px-4 pb-1.5 select-none overflow-hidden">
      <span
        className="text-[9px] tracking-[0.24em] text-[#525252] font-mono uppercase whitespace-nowrap transition-opacity duration-300"
        style={{ opacity: expanded ? 1 : 0 }}
      >
        {children}
      </span>
    </div>
  );
}

const TOP_NAV = [
  { to: "/panel", label: "Panel", icon: Home, testid: "nav-dashboard" },
  { to: "/gecmis", label: "Sorgu Geçmişi", icon: History, testid: "nav-history" },
];

const BOTTOM_NAV = [
  { to: "/magaza", label: "Mağaza", icon: ShoppingBag, testid: "nav-shop" },
  { to: "/duyurular", label: "Duyurular", icon: Megaphone, testid: "nav-announcements" },
  { to: "/profil", label: "Profil", icon: User, testid: "nav-profile" },
];

const RESOURCE_NAV = [
  { to: "/yardim", label: "Yardım Merkezi", icon: LifeBuoy, testid: "nav-help" },
  { to: "/api-docs", label: "API Dokümanı", icon: BookOpen, testid: "nav-api" },
  {
    to: "https://discord.gg/KbWUeh6SP2",
    label: "Discord Topluluğu",
    icon: Radio,
    testid: "nav-community",
    external: true,
  },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [hovered, setHovered] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const expanded = hovered || mobileOpen;

  const platformItems = PLATFORM_ORDER.map((p) => ({
    to: `/checker/${p}`,
    label: PLATFORM_META[p].short,
    Icon: PLATFORM_META[p].Icon,
    color: PLATFORM_META[p].color,
    testid: `nav-platform-${p}`,
  }));

  const renderItem = (item, key) => {
    if (item.external) {
      return (
        <a
          key={key || item.to}
          href={item.to}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={item.testid}
          title={item.label}
          className="gc-rail-item"
          onClick={() => setMobileOpen(false)}
        >
          <span className="gc-rail-icon" style={item.color ? { color: item.color } : undefined}>
            {item.Icon ? <item.Icon size={15} /> : <item.icon size={15} strokeWidth={1.8} />}
          </span>
          <span
            className={`gc-rail-label transition-opacity duration-200 ${
              expanded ? "opacity-100" : "opacity-0"
            }`}
          >
            {item.label}
          </span>
        </a>
      );
    }
    return (
      <NavLink
        key={key || item.to}
        to={item.to}
        data-testid={item.testid}
        title={item.label}
        className={({ isActive }) => `gc-rail-item ${isActive ? "active" : ""}`}
        onClick={() => setMobileOpen(false)}
      >
        <span className="gc-rail-icon" style={item.color ? { color: item.color } : undefined}>
          {item.Icon ? <item.Icon size={15} /> : <item.icon size={15} strokeWidth={1.8} />}
        </span>
        <span
          className={`gc-rail-label transition-opacity duration-200 ${
            expanded ? "opacity-100" : "opacity-0"
          }`}
        >
          {item.label}
        </span>
      </NavLink>
    );
  };

  return (
    <>
      <button
        data-testid="sidebar-mobile-toggle"
        className="fixed top-3 left-3 z-50 md:hidden w-9 h-9 flex items-center justify-center bg-[#050505] border border-[#1a1a1a] text-[#EDEDED] rounded-lg"
        onClick={() => setMobileOpen((v) => !v)}
        aria-label="Menüyü aç"
      >
        {mobileOpen ? <X size={14} /> : <Menu size={14} />}
      </button>

      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/70 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <aside
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className={`fixed md:sticky top-0 left-0 h-screen z-40 border-r border-[#141414] bg-[#040404] overflow-hidden transform-gpu will-change-[width]
          transition-[width,transform] duration-[600ms]
          ${expanded ? "w-56" : "w-[64px]"}
          ${mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}
        `}
        style={{ transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)" }}
        data-testid="sidebar"
        data-expanded={expanded}
      >
        <div className="flex flex-col h-full">
          {/* Brand */}
          <div className="px-2 py-2 border-b border-[#141414] flex items-center gap-3 min-h-[72px]">
            <img
              src="/assets/gulyan-favicon.png"
              alt="Gulyan"
              className="w-12 h-12 shrink-0 object-contain drop-shadow-[0_0_12px_rgba(157,78,221,0.25)]"
              data-testid="sidebar-logo"
            />
            <div
              className={`transition-opacity duration-500 ${expanded ? "opacity-100" : "opacity-0"} whitespace-nowrap`}
            >
              <div className="font-heading text-[17px] font-bold text-white leading-none tracking-tight">
                GULYAN
              </div>
              <div className="text-[10px] tracking-[0.28em] text-[#9D4EDD] font-mono mt-1.5">
                CHECKER
              </div>
            </div>
          </div>

          <nav className="flex-1 overflow-y-auto overflow-x-hidden py-1">
            <SectionLabel expanded={expanded}>Genel</SectionLabel>
            {TOP_NAV.map((i) => renderItem(i))}

            <SectionLabel expanded={expanded}>Sorgu Modülleri</SectionLabel>
            {platformItems.map((i) => renderItem(i))}

            <SectionLabel expanded={expanded}>Hesap</SectionLabel>
            {BOTTOM_NAV.map((i) => renderItem(i))}
            {user?.role === "admin" &&
              renderItem({
                to: "/admin",
                label: "Admin Panel",
                icon: ShieldAlert,
                testid: "nav-admin",
              })}

            <SectionLabel expanded={expanded}>Kaynaklar</SectionLabel>
            {RESOURCE_NAV.map((i) => renderItem(i))}
          </nav>

          {/* Bottom user block */}
          <div className="px-2 py-3 border-t border-[#141414]">
            <div className="flex items-center gap-2 px-2">
              <div className="w-8 h-8 shrink-0 flex items-center justify-center border border-[#1a1a1a] bg-[#0a0a0a] font-heading font-bold text-white text-xs rounded-lg">
                {user?.username?.[0]?.toUpperCase() || "U"}
              </div>
              <div
                className={`flex-1 min-w-0 transition-opacity duration-500 ${expanded ? "opacity-100" : "opacity-0"}`}
              >
                <div className="text-xs text-white truncate" data-testid="sidebar-username">
                  {user?.username}
                </div>
                <div className="text-[9px] font-mono text-[#A1A1AA] uppercase tracking-wider">
                  {user?.role === "admin" ? "ADMIN" : user?.membership_tier?.toUpperCase()}
                </div>
              </div>
              {expanded && (
                <button
                  data-testid="sidebar-logout"
                  className="w-8 h-8 flex items-center justify-center border border-[#1a1a1a] text-[#A1A1AA] hover:border-[#9D4EDD] hover:text-white transition-colors rounded-lg"
                  onClick={handleLogout}
                  title="Çıkış yap"
                >
                  <LogOut size={12} />
                </button>
              )}
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}
